import { getApplication } from '../../../../../../javascript/models/Application';

type App = {
  set?: (k: string, v: unknown) => void;
  get?: (k: string) => unknown;
  tasksCollection?: {
    add: (t: unknown) => void;
    get: (id: number) => { set: (attrs: object) => void };
  };
};

type PendingMergePayload = Record<string, unknown> & {
  bulk_request_id?: number;
  bulkRequestId?: number;
  archived_task_ids?: number[];
  archivedTaskIds?: number[];
  new_task?: Record<string, unknown>;
  newTask?: Record<string, unknown>;
};

export type HandleBulkRequestSuccessParams = {
  id: number;
  moving: number | boolean;
  merging: number | boolean;
  deleting: number | boolean;
  lastBulkUpdate: { targetState: unknown; taskIds: number[]; id?: number };
  setMoving: (id: number) => void;
  setMerging: (id: number) => void;
  setDeleting: (id: number) => void;
  setLastBulkUpdate: (update: unknown) => void;
  onResetSelectedTasks: () => void;
  handleClear: () => void;
};

function processPendingMerge(app: App, id: number): void {
  const pendingMerge = app?.get?.('pending_task_merge') as
    | PendingMergePayload
    | null;

  if (
    !pendingMerge ||
    String(pendingMerge.bulk_request_id ?? pendingMerge.bulkRequestId) !==
      String(id)
  ) {
    return;
  }

  app?.set?.('pending_task_merge', null);

  const archivedIds =
    (pendingMerge.archived_task_ids as number[] | undefined) ??
    (pendingMerge.archivedTaskIds as number[] | undefined) ??
    [];
  const newTaskData = pendingMerge.new_task ?? pendingMerge.newTask;
  const tasksCollection = (app as App)?.tasksCollection;

  const mergeData = {
    bulkRequestId: id,
    archivedTaskIds: archivedIds,
    newTaskId: (newTaskData as Record<string, unknown>)?.id as
      | number
      | undefined
  };
  app?.set?.('merge_completed', mergeData);
  // Dispatch merge_completed BEFORE updating the collection so the
  // BulkActionsBar's useMergeCompletion handler receives the event
  // before Backbone collection updates trigger view refresh (which
  // unmounts the component). The modal has a retry to find the task
  // once it's added to the collection.
  window.dispatchEvent(
    new CustomEvent('bugherd:merge_completed', { detail: mergeData })
  );

  if (newTaskData && tasksCollection) {
    tasksCollection.add(newTaskData);
  }
  if (tasksCollection) {
    archivedIds.forEach((taskId: number) => {
      const task = tasksCollection.get(taskId);
      if (task) task.set({ status_id: 5, column_id: null });
    });
  }
}

export function handleBulkRequestSuccess({
  id,
  moving,
  merging,
  deleting,
  lastBulkUpdate,
  setMoving,
  setMerging,
  setDeleting,
  setLastBulkUpdate,
  onResetSelectedTasks,
  handleClear
}: HandleBulkRequestSuccessParams): void {
  if (moving === true) {
    setMoving(id);
    onResetSelectedTasks();
  }

  if (merging === true) {
    setMerging(id);
    const app = getApplication() as App;
    app?.set?.('merging_bulk_request_id', id);
    app?.set?.('merging_pending', false);
    processPendingMerge(app, id);
  }

  if (deleting === true) {
    setDeleting(id);
    onResetSelectedTasks();
  }

  setLastBulkUpdate({ ...lastBulkUpdate, id });
  handleClear();
}

export function handleBulkRequestError(merging: number | boolean): void {
  if (merging === true) {
    (getApplication() as App)?.set?.('merging_pending', false);
  }
}

export function prepareForMerge(): void {
  (getApplication() as App)?.set?.('merging_pending', true);
}
